"use client";

import { useEffect, useRef, useState } from "react";
import { AD_UNITS } from "@/ads/adUnits";
import AdErrorBoundary from "@/ads/AdErrorBoundary";
import { ensureGptReady } from "@/ads/gptInit";
import { logger } from "@/app/utilities/logger";

type Props = {
  adUnit: typeof AD_UNITS[keyof typeof AD_UNITS];
  className?: string;
  rootMargin?: string;
  loadDelayMs?: number;
};

export default function AdLoader(props: Props) {
  return (
    <AdErrorBoundary>
      <AdLoaderInner {...props} />
    </AdErrorBoundary>
  );
}

function AdLoaderInner({
  adUnit,
  className = "",
  rootMargin = "300px",
  loadDelayMs = 0,
}: Props) {
  const config = adUnit;
  const [isLoading, setIsLoading] = useState(true);
  const [isEmpty, setIsEmpty] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  // observe ad render for IntersectionObserver 
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      {
        rootMargin,
      }
    );

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [rootMargin]);

  useEffect(() => {
    if (!shouldLoad) return;

    let delayId: ReturnType<typeof setTimeout> | null = null;
    let isCancelled = false;
    ensureGptReady();

    let slot: any = null;
    let timeoutId: any = null;

    const handleSlotRenderEnded = (event: any) => {
      if (event.slot === slot) {
        if (timeoutId) clearTimeout(timeoutId);
        setIsLoading(false);
        if (event.isEmpty) {
          setIsEmpty(true);
        }
      }
    };

    const loadAd = () => {
      if (isCancelled) return;

      window.googletag.cmd.push(() => {
        window.googletag.pubads().addEventListener("slotRenderEnded", handleSlotRenderEnded);

        slot = window.googletag
          .defineSlot(
            config.adUnitPath,
            config.sizes,
            config.divId
          )
          ?.addService(window.googletag.pubads());

        window.googletag.display(config.divId);
      });

      // Fallback: if ad takes more than 8 seconds or fails to load (e.g. adblock), stop loading and mark empty
      timeoutId = setTimeout(() => {
        setIsLoading(false);
        setIsEmpty(true);
      }, 8000);
    };

    if (loadDelayMs > 0) {
      delayId = setTimeout(loadAd, loadDelayMs);
    } else {
      loadAd();
    }

    return () => {
      isCancelled = true;
      if (delayId) clearTimeout(delayId);
      if (timeoutId) clearTimeout(timeoutId);
      window.googletag.cmd.push(() => {
        // This queued callback can run after React has already unmounted the
        // slot's container (fast route change), so GPT's own teardown can
        // race with React's — never let it throw past this boundary.
        try {
          window.googletag.pubads().removeEventListener("slotRenderEnded", handleSlotRenderEnded);
          if (slot) {
            window.googletag.destroySlots([slot]);
          }
        } catch (err) {
          logger.error("Error tearing down GPT slot", { errorMessage: err instanceof Error ? err.message : String(err) });
        }
      });
    };
  }, [config, shouldLoad, loadDelayMs]);

  // Reserve the tallest height across every size GPT could pick for this slot so
  // the box never resizes between the loading placeholder and the filled ad (avoids CLS).
  const sizes = config.sizes ?? [];
  const reservedHeight = sizes.length
    ? Math.max(...sizes.map(([, height]) => height))
    : 250;

  return (
    <div ref={containerRef} className={isEmpty ? "hidden" : className}>
      <div
        className="relative flex items-center justify-center overflow-hidden transition-all duration-300"
        style={isEmpty ? undefined : { height: reservedHeight }}
      >
        {isLoading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="relative flex items-center justify-center">
              {/* Spinning Loader Ring */}
              <div className="w-12 h-12 rounded-full border-4 border-gray-100 border-t-[#008C8B] animate-spin"></div>
              {/* Center Text */}
              <span className="absolute text-[10px] font-extrabold tracking-widest text-gray-500">AD</span>
            </div>
          </div>
        )}
        <div
          className={`flex items-center justify-center w-full h-full ${isLoading ? "opacity-0" : ""}`}
          id={config.divId}
        />
      </div>
    </div>
  );
}
